import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Shield, FileText, Users, Database, BarChart3,
  CheckCircle, AlertTriangle, XCircle, Send, UserCheck,
  Loader2, Copy, Plus, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: Shield },
  { id: "sources", label: "Sources", icon: Database },
  { id: "referees", label: "Referees", icon: Users },
  { id: "analysis", label: "Analysis", icon: BarChart3 },
  { id: "report", label: "Report", icon: FileText },
  { id: "review", label: "Review", icon: CheckCircle },
];

export default function VettingCaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [analysis, setAnalysis] = useState(null);
  const [qaFlags, setQaFlags] = useState([]);
  const [report, setReport] = useState("");
  const [actionLoading, setActionLoading] = useState("");

  // Referee form
  const [showRefereeForm, setShowRefereeForm] = useState(false);
  const [refereeForm, setRefereeForm] = useState({
    full_name: "", contact_email: "", relationship: "",
    relationship_years: 1, how_identified: "subject_referred",
  });

  // Source form
  const [showSourceForm, setShowSourceForm] = useState(false);
  const [sourceForm, setSourceForm] = useState({
    source_type: "bank", source_name: "", payload: "{}",
  });

  // Review form
  const [reviewerId, setReviewerId] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const loadCase = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vetting/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      setCaseData(res.data.case);
    } catch (e) {
      toast.error("Failed to load case");
      navigate("/vetting");
    } finally {
      setLoading(false);
    }
  }, [caseId, navigate]);

  useEffect(() => { loadCase(); }, [loadCase]);

  async function runAnalysis() {
    setActionLoading("analyze");
    try {
      const res = await axios.post(`${API}/vetting/cases/${caseId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      setAnalysis(res.data);
      toast.success("Analysis complete");
      loadCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setActionLoading("");
    }
  }

  async function runQaFlags() {
    setActionLoading("qa");
    try {
      const res = await axios.get(`${API}/vetting/cases/${caseId}/qa-flags`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      setQaFlags(res.data.qa_flags || []);
      toast.success(`${res.data.count} QA flags found`);
    } catch (e) {
      toast.error("QA check failed");
    } finally {
      setActionLoading("");
    }
  }

  async function classify() {
    setActionLoading("classify");
    try {
      const res = await axios.post(`${API}/vetting/cases/${caseId}/classify`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      toast.success(`Classification: ${res.data.classification}`);
      loadCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Classification failed");
    } finally {
      setActionLoading("");
    }
  }

  async function generateReport() {
    setActionLoading("report");
    try {
      const res = await axios.get(`${API}/vetting/cases/${caseId}/report`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      setReport(res.data.report);
      setActiveTab("report");
    } catch (e) {
      toast.error("Report generation failed");
    } finally {
      setActionLoading("");
    }
  }

  async function addReferee(e) {
    e.preventDefault();
    setActionLoading("referee");
    try {
      await axios.post(`${API}/vetting/cases/${caseId}/referees`, refereeForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      toast.success("Referee added");
      setShowRefereeForm(false);
      setRefereeForm({ full_name: "", contact_email: "", relationship: "", relationship_years: 1, how_identified: "subject_referred" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add referee");
    } finally {
      setActionLoading("");
    }
  }

  async function ingestSource(e) {
    e.preventDefault();
    setActionLoading("source");
    try {
      let payload;
      try { payload = JSON.parse(sourceForm.payload); }
      catch { toast.error("Invalid JSON in payload"); setActionLoading(""); return; }

      await axios.post(`${API}/vetting/cases/${caseId}/ingest`,
        { source_type: sourceForm.source_type, source_name: sourceForm.source_name, collector: "manual", payload },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true }
      );
      toast.success("Source ingested (processing in background)");
      setShowSourceForm(false);
      setSourceForm({ source_type: "bank", source_name: "", payload: "{}" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ingest failed");
    } finally {
      setActionLoading("");
    }
  }

  async function submitForReview() {
    if (!reviewerId.trim()) { toast.error("Enter a reviewer ID"); return; }
    setActionLoading("submit");
    try {
      await axios.post(`${API}/vetting/cases/${caseId}/submit-review`,
        { reviewer_id: reviewerId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true }
      );
      toast.success("Submitted for review");
      loadCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setActionLoading("");
    }
  }

  async function approveCase() {
    setActionLoading("approve");
    try {
      await axios.post(`${API}/vetting/cases/${caseId}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true
      });
      toast.success("Classification approved");
      loadCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setActionLoading("");
    }
  }

  async function rejectCase() {
    if (!rejectReason.trim()) { toast.error("Enter a rejection reason"); return; }
    setActionLoading("reject");
    try {
      await axios.post(`${API}/vetting/cases/${caseId}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true }
      );
      toast.success("Classification rejected — case returned to draft");
      loadCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rejection failed");
    } finally {
      setActionLoading("");
    }
  }

  async function copyReport() {
    await navigator.clipboard.writeText(report);
    toast.success("Report copied to clipboard");
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="p-6 text-center text-slate-400">
        <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <p>Case not found</p>
      </div>
    );
  }

  const c = caseData;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate("/vetting")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2">
            <ArrowLeft className="w-4 h-4" /> Back to Cases
          </button>
          <h1 className="text-2xl font-bold text-slate-800 font-['Barlow_Condensed'] tracking-wide">
            {c.case_number}: {c.subject_name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {c.subject_id_type}: {c.subject_id_value} &middot; Created {new Date(c.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {c.classification && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700">
              {c.classification}
            </span>
          )}
          <span className="px-3 py-1 rounded-full text-sm bg-slate-100 text-slate-600">
            {c.status?.replace(/_/g, " ")}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── TAB: Overview ──────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-800 mb-4">Case Details</h2>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div><dt className="text-slate-400">Case Number</dt><dd className="font-mono">{c.case_number}</dd></div>
                <div><dt className="text-slate-400">Status</dt><dd>{c.status?.replace(/_/g, " ")}</dd></div>
                <div><dt className="text-slate-400">Subject</dt><dd>{c.subject_name}</dd></div>
                <div><dt className="text-slate-400">ID</dt><dd>{c.subject_id_type}: {c.subject_id_value}</dd></div>
                <div><dt className="text-slate-400">Investigator</dt><dd>{c.investigator_id}</dd></div>
                <div><dt className="text-slate-400">Reviewer</dt><dd>{c.reviewer_id || "—"}</dd></div>
                <div><dt className="text-slate-400">Consent</dt><dd>{c.consent_obtained ? "Obtained" : "Pending"}</dd></div>
                <div><dt className="text-slate-400">Classification</dt><dd>{c.classification || "—"}</dd></div>
              </dl>
            </div>
          </div>

          {/* Actions sidebar */}
          <div className="space-y-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-semibold text-slate-800 text-sm mb-3">Actions</h3>
              <div className="space-y-2">
                <button onClick={runAnalysis} disabled={actionLoading === "analyze"}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                  {actionLoading === "analyze" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                  Run Analysis
                </button>
                <button onClick={runQaFlags} disabled={actionLoading === "qa"}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50">
                  {actionLoading === "qa" ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                  QA Checks
                </button>
                <button onClick={classify} disabled={actionLoading === "classify"}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                  {actionLoading === "classify" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Classify
                </button>
                <button onClick={generateReport} disabled={actionLoading === "report"}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50">
                  {actionLoading === "report" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Sources ───────────────────────────────────────── */}
      {activeTab === "sources" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Data Sources</h2>
            <button onClick={() => setShowSourceForm(!showSourceForm)}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
              <Plus className="w-4 h-4" /> Add Source
            </button>
          </div>

          {showSourceForm && (
            <form onSubmit={ingestSource} className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Source Type</label>
                  <select value={sourceForm.source_type} onChange={e => setSourceForm({...sourceForm, source_type: e.target.value})}
                    className="w-full px-2 py-2 border border-slate-200 rounded text-sm">
                    <option value="bank">Bank Data</option>
                    <option value="social_media">Social Media</option>
                    <option value="public_record">Public Record</option>
                    <option value="subject_kyc">Subject KYC</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Source Name</label>
                  <input type="text" value={sourceForm.source_name} onChange={e => setSourceForm({...sourceForm, source_name: e.target.value})}
                    className="w-full px-2 py-2 border border-slate-200 rounded text-sm" placeholder="e.g. FNB Account" required />
                </div>
                <div className="flex items-end">
                  <button type="submit" disabled={actionLoading === "source"}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                    {actionLoading === "source" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ingest"}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Payload (JSON)</label>
                <textarea value={sourceForm.payload} onChange={e => setSourceForm({...sourceForm, payload: e.target.value})}
                  rows={6} className="w-full px-2 py-2 border border-slate-200 rounded text-sm font-mono" />
              </div>
            </form>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-400">
            <Database className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Use the "Add Source" button above to ingest data. Sources are processed asynchronously by LLM.</p>
          </div>
        </div>
      )}

      {/* ── TAB: Referees ──────────────────────────────────────── */}
      {activeTab === "referees" && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Referees</h2>
            <button onClick={() => setShowRefereeForm(!showRefereeForm)}
              className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">
              <Plus className="w-4 h-4" /> Add Referee
            </button>
          </div>

          {showRefereeForm && (
            <form onSubmit={addReferee} className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Full Name</label>
                  <input type="text" value={refereeForm.full_name} onChange={e => setRefereeForm({...refereeForm, full_name: e.target.value})}
                    className="w-full px-2 py-2 border border-slate-200 rounded text-sm" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                  <input type="email" value={refereeForm.contact_email} onChange={e => setRefereeForm({...refereeForm, contact_email: e.target.value})}
                    className="w-full px-2 py-2 border border-slate-200 rounded text-sm" required />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">Relationship</label>
                  <input type="text" value={refereeForm.relationship} onChange={e => setRefereeForm({...refereeForm, relationship: e.target.value})}
                    className="w-full px-2 py-2 border border-slate-200 rounded text-sm" placeholder="e.g. Colleague, Manager" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Years Known</label>
                  <input type="number" value={refereeForm.relationship_years} onChange={e => setRefereeForm({...refereeForm, relationship_years: parseInt(e.target.value) || 1})}
                    className="w-full px-2 py-2 border border-slate-200 rounded text-sm" min="0" />
                </div>
              </div>
              <button type="submit" disabled={actionLoading === "referee"}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                {actionLoading === "referee" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Referee"}
              </button>
            </form>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-400">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Referee management will be available once referees are added.</p>
          </div>
        </div>
      )}

      {/* ── TAB: Analysis ──────────────────────────────────────── */}
      {activeTab === "analysis" && (
        <div className="space-y-4">
          {!analysis ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Run analysis from the Overview tab to see results here.</p>
            </div>
          ) : (
            <>
              {/* Risk Matrix */}
              {analysis.risk_matrix && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Risk Matrix</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { label: "Financial", key: "financial_score", color: "bg-red-500" },
                      { label: "Social", key: "social_score", color: "bg-amber-500" },
                      { label: "Legal", key: "legal_score", color: "bg-purple-500" },
                      { label: "Identity", key: "identity_score", color: "bg-blue-500" },
                      { label: "Contradiction", key: "contradiction_score", color: "bg-orange-500" },
                      { label: "Overall", key: "overall_score", color: "bg-indigo-600" },
                      { label: "Completeness", key: "data_completeness", color: "bg-teal-500" },
                      { label: "Confidence", key: "confidence", color: "bg-green-500" },
                    ].map(item => (
                      <div key={item.key} className="bg-app rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1">{item.label}</p>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 rounded-full ${item.color}`}
                            style={{ width: `${(analysis.risk_matrix[item.key] || 0) * 100}%`, minWidth: analysis.risk_matrix[item.key] > 0 ? "8px" : "0" }} />
                          <span className="text-sm font-mono font-medium">{analysis.risk_matrix[item.key]?.toFixed(2) || "0.00"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contradictions */}
              {analysis.contradictions?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Contradictions Found</h3>
                  <div className="space-y-2">
                    {analysis.contradictions.map((c, i) => (
                      <div key={i} className={`p-3 rounded-lg border text-sm ${
                        c.severity === "high" ? "bg-red-50 border-red-200" :
                        c.severity === "medium" ? "bg-amber-50 border-amber-200" :
                        "bg-yellow-50 border-yellow-200"
                      }`}>
                        <p className="font-medium">{c.claim}</p>
                        <p className="text-slate-600 mt-1">{c.explanation}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {c.source_a?.source}: {c.source_a?.value} &mdash; {c.source_b?.source}: {c.source_b?.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* QA Flags */}
              {qaFlags.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Quality Check Flags</h3>
                  <div className="space-y-2">
                    {qaFlags.map((flag, i) => (
                      <div key={i} className={`p-3 rounded-lg border text-sm ${
                        flag.severity === "blocker" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"
                      }`}>
                        <div className="flex items-center gap-2">
                          {flag.severity === "blocker"
                            ? <XCircle className="w-4 h-4 text-red-500" />
                            : <AlertTriangle className="w-4 h-4 text-amber-500" />}
                          <span className="font-medium">{flag.flag_type?.replace(/_/g, " ")}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            flag.severity === "blocker" ? "bg-red-200 text-red-800" : "bg-amber-200 text-amber-800"
                          }`}>{flag.severity}</span>
                        </div>
                        <p className="text-slate-600 mt-1 ml-6">{flag.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── TAB: Report ────────────────────────────────────────── */}
      {activeTab === "report" && (
        <div>
          {!report ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Generate a report from the Overview tab to see it here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-800">Vetting Report</h2>
                <button onClick={copyReport}
                  className="flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-app">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap font-sans text-slate-700 leading-relaxed">
                {report}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Review ────────────────────────────────────────── */}
      {activeTab === "review" && (
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <h2 className="text-lg font-semibold text-slate-800">Review Workflow</h2>

            {c.status === "qa_review" ? (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    This case is in QA Review. A reviewer can approve or reject the classification.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reject Reason</label>
                  <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Reason for rejection (required for reject)..." />
                </div>

                <div className="flex gap-3">
                  <button onClick={approveCase} disabled={actionLoading === "approve"}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
                    {actionLoading === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Approve
                  </button>
                  <button onClick={rejectCase} disabled={actionLoading === "reject"}
                    className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50">
                    {actionLoading === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                    Reject
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-app border border-slate-200 rounded-lg">
                  <p className="text-sm text-slate-600">
                    Current status: <strong>{c.status?.replace(/_/g, " ")}</strong>.
                    {c.status === "classified"
                      ? " This case has been classified."
                      : " Run analysis and classification first, then submit for review."}
                  </p>
                </div>

                {(c.status === "analyzing" || c.status === "classified") && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Reviewer User ID</label>
                      <input type="text" value={reviewerId} onChange={e => setReviewerId(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Enter reviewer's user ID..." />
                    </div>
                    <button onClick={submitForReview} disabled={actionLoading === "submit"}
                      className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
                      {actionLoading === "submit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Submit for Review
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
